import { apiClient } from "./client";
import type { ExerciseMode, LocationKind, SlotTemplate, Specialty } from "./types";

// ----------------------------------------------------------------------------
// Specialties
// ----------------------------------------------------------------------------
export async function listSpecialties(isActive?: boolean): Promise<Specialty[]> {
  const { data } = await apiClient.get<Specialty[]>("/api/v1/specialties", {
    params: isActive !== undefined ? { is_active: isActive } : undefined,
  });
  return data;
}

export type CreateSpecialtyPayload = {
  code: string;
  label: string;
  color?: string | null;
  description?: string | null;
};

export async function createSpecialty(p: CreateSpecialtyPayload): Promise<Specialty> {
  const { data } = await apiClient.post<Specialty>("/api/v1/specialties", p);
  return data;
}

export async function updateSpecialty(
  id: string,
  p: Partial<CreateSpecialtyPayload> & { is_active?: boolean },
): Promise<Specialty> {
  const { data } = await apiClient.patch<Specialty>(`/api/v1/specialties/${id}`, p);
  return data;
}

export async function deleteSpecialty(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/specialties/${id}`);
}

// ----------------------------------------------------------------------------
// Exercise modes
// ----------------------------------------------------------------------------
export async function listExerciseModes(isActive?: boolean): Promise<ExerciseMode[]> {
  const { data } = await apiClient.get<ExerciseMode[]>("/api/v1/exercise-modes", {
    params: isActive !== undefined ? { is_active: isActive } : undefined,
  });
  return data;
}

export type CreateExerciseModePayload = {
  code: string;
  label: string;
  color?: string | null;
  location_kind?: LocationKind;
  base_weight?: number;
  on_call_weight_idle?: number | null;
  on_call_weight_triggered?: number | null;
  counts_as_worked?: boolean;
};

export async function createExerciseMode(p: CreateExerciseModePayload): Promise<ExerciseMode> {
  const { data } = await apiClient.post<ExerciseMode>("/api/v1/exercise-modes", p);
  return data;
}

export async function updateExerciseMode(
  id: string,
  p: Partial<CreateExerciseModePayload> & { is_active?: boolean },
): Promise<ExerciseMode> {
  const { data } = await apiClient.patch<ExerciseMode>(`/api/v1/exercise-modes/${id}`, p);
  return data;
}

export async function deleteExerciseMode(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/exercise-modes/${id}`);
}

// ----------------------------------------------------------------------------
// Slot templates (scoped by organization)
// ----------------------------------------------------------------------------
export async function listSlotTemplates(
  organizationId?: string,
  isActive?: boolean,
): Promise<SlotTemplate[]> {
  const params: Record<string, string | boolean> = {};
  if (organizationId) params.organization_id = organizationId;
  if (isActive !== undefined) params.is_active = isActive;
  const { data } = await apiClient.get<SlotTemplate[]>("/api/v1/slot-templates", { params });
  return data;
}

export type CreateSlotTemplatePayload = {
  organization_id: string;
  label: string;
  start_time: string; // HH:MM:SS
  end_time: string;
  duration_min: number;
  is_night?: boolean;
  is_overnight?: boolean;
  weight_modifier?: number;
  kind?: import("./types").SlotKind;
  target_headcount?: number;
};

export async function createSlotTemplate(p: CreateSlotTemplatePayload): Promise<SlotTemplate> {
  const { data } = await apiClient.post<SlotTemplate>("/api/v1/slot-templates", p);
  return data;
}

export async function updateSlotTemplate(
  id: string,
  p: Partial<Omit<CreateSlotTemplatePayload, "organization_id">> & { is_active?: boolean },
): Promise<SlotTemplate> {
  const { data } = await apiClient.patch<SlotTemplate>(`/api/v1/slot-templates/${id}`, p);
  return data;
}

export type BulkTargetPayload = {
  organization_id: string;
  from_time: string; // HH:MM:SS
  to_time: string;
  kind?: import("./types").SlotKind;
  target_headcount: number;
};

/** Apply a required headcount to every slot inside a time range. */
export async function bulkSetSlotTarget(
  p: BulkTargetPayload,
): Promise<{ updated: number; slot_ids: string[] }> {
  const { data } = await apiClient.post<{ updated: number; slot_ids: string[] }>(
    "/api/v1/slot-templates/bulk-target",
    p,
  );
  return data;
}

export async function deleteSlotTemplate(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/slot-templates/${id}`);
}
